import type { BaseWorkorder } from '../../../models/WorkOrder/types'
import type {
    VendorSubPricingDetailResponse,
    VendorSubPricingLine,
    VendorSubPricingServiceBlock,
} from '../../../models/VendorSubPricing/types'

/** Narrow workorder shape for sub-contract price suggestion (assign subs, etc.). */
export type SubContractPriceWorkorderSlice = Pick<
    BaseWorkorder,
    'service_id' | 'fully_vacant' | 'unit_config_id' | 'service_area_list'
>

/** Parse API sub-vendor contract price string; empty/null → 0. */
export function parseSubVendorPriceString(
    price: string | null | undefined,
): number {
    if (price == null || price === '') return 0
    const n = Number(price)
    return Number.isFinite(n) ? n : 0
}

/**
 * Suggested sub-contract total in dollars from pricing-detail matrix (no use_max_price cap).
 * `fully_vacant` → unit row for `unit_config_id`; else sum area rows for each `service_area_list` entry.
 * Returns null when detail or service block is missing, or `service_id` is null.
 */
export function getSuggestedSubContractPriceDollars(
    detail: VendorSubPricingDetailResponse | null | undefined,
    wo: SubContractPriceWorkorderSlice,
): number | null {
    if (!detail || wo.service_id == null) return null
    const block = findServiceBlock(detail, wo.service_id)
    if (!block) return null

    if (wo.fully_vacant) {
        const line = block.units.find(
            (l) => l.unit_config?.id === wo.unit_config_id,
        )
        if (!line) return 0
        return parseSubVendorPriceString(line.sub_vendor_contract?.price)
    }

    let total = 0
    for (const sa of wo.service_area_list ?? []) {
        const line = block.areas.find(
            (l) => l.area_config?.id === sa.area_config_id,
        )
        if (line) {
            total += parseSubVendorPriceString(line.sub_vendor_contract?.price)
        }
    }
    return total
}

/** Whole dollars for UI that only accepts integer prices (e.g. Assign Subs modal). */
export function suggestedSubContractPriceWholeDollars(
    detail: VendorSubPricingDetailResponse | null | undefined,
    wo: SubContractPriceWorkorderSlice,
): number | null {
    const d = getSuggestedSubContractPriceDollars(detail, wo)
    if (d === null) return null
    return Math.round(d)
}

/** Lines where crew sub-contract row exists vs total lines (areas + units only for this slice). */
export function formatSubVendorCoverageHint(
    block: VendorSubPricingServiceBlock | undefined,
): string {
    if (!block) return '—'
    const lines = [...block.areas, ...block.units]
    const total = lines.length
    if (total === 0) return 'No rows'
    const set = lines.filter((l) => l.sub_vendor_contract != null).length
    return `${set}/${total} sub-contracts`
}

export function findServiceBlock(
    matrix: VendorSubPricingDetailResponse | null,
    serviceId: number,
): VendorSubPricingServiceBlock | undefined {
    return matrix?.services.find((s) => s.id === serviceId)
}

export function parseMoneyDisplay(raw: string | undefined | null): string {
    if (raw == null || raw === '') return '—'
    const n = Number(raw)
    if (Number.isNaN(n)) return raw
    return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
    }).format(n)
}

/** Empty string or up to two decimal places (same rule as vendor pricing link form). */
export function isValidSubPricingDecimal(value: string): boolean {
    return value === '' || /^\d*(\.\d{0,2})?$/.test(value)
}

export function baselineCrewPriceFromLine(line: VendorSubPricingLine): string {
    return line.sub_vendor_contract?.price ?? ''
}

export function visibleMatrixLines(
    block: VendorSubPricingServiceBlock | undefined,
): VendorSubPricingLine[] {
    if (!block) return []
    return [...block.areas, ...block.units]
}

export function buildCrewPriceDraftBaseline(
    lines: readonly VendorSubPricingLine[],
): Record<number, string> {
    const map: Record<number, string> = {}
    for (const line of lines) {
        map[line.service_contract.id] = baselineCrewPriceFromLine(line)
    }
    return map
}
